import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/shared/SEOHead";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  Signal, DollarSign, Users, Award, ChevronRight, CheckCircle,
  ArrowRight, Smartphone, Headphones, TrendingUp, Megaphone, Wrench,
  ChevronLeft,
} from "lucide-react";
import { getPageContent } from "@/lib/api";
import { WhyChoosePmw } from "@/components/shared/WhyChoosePmw";
import { AwardsSection } from "@/components/shared/AwardsSection";

const DEFAULT_HERO_SLIDES = [
  "https://static.prod-images.emergentagent.com/jobs/6ed282eb-cd3c-49c8-9c53-32e6ae020576/images/4da47de0fca2faaac97c46212c717d1083e1c30715e670f1abf538ad40c97eb2.png",
  "https://static.prod-images.emergentagent.com/jobs/6ed282eb-cd3c-49c8-9c53-32e6ae020576/images/f82c97209f95fd1d7bef8b30bc500d1a75ce720ccc30bbcd83392e8f85e260f1.png",
  "https://static.prod-images.emergentagent.com/jobs/6ed282eb-cd3c-49c8-9c53-32e6ae020576/images/3a2595611e1de4164f5a5dd3434aff3f06fbdc5a8c35e6c13b05ed34eeb63f2d.png",
  "https://static.prod-images.emergentagent.com/jobs/6ed282eb-cd3c-49c8-9c53-32e6ae020576/images/e92eadffc4bae0b9535d309f3d482fed66e92950969426f06719b7e38940c203.png",
  "https://static.prod-images.emergentagent.com/jobs/6ed282eb-cd3c-49c8-9c53-32e6ae020576/images/b981f7218de0318a1e6160f5da7f248b76fa3bf302c2ee7386167e26b6a2030a.png",
];

const ICON_MAP = {
  Signal, DollarSign, Users, Award, Smartphone, Headphones, TrendingUp,
  ChevronRight, CheckCircle, ArrowRight, Megaphone, Wrench,
  Loudspeaker: Megaphone, Repair: Wrench,
};

// Image map for feature cards
const FEATURE_IMAGES = {
  Signal: "https://pmw-coming-soon.preview.emergentagent.com/api/files/prime-media-wireless/images/ee35d495-7c33-483f-8030-2f95714528a2.jpg",
  DollarSign: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&h=600&fit=crop&q=90",
  Users: "https://pmw-coming-soon.preview.emergentagent.com/api/files/prime-media-wireless/images/fb08d5ae-ad83-4fe9-9cf6-68675bf82dc8.jpg",
  Award: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&h=600&fit=crop&q=90",
  Repair: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop&q=90",
  Loudspeaker: "https://images.unsplash.com/photo-1533750349088-cd871a92f312?w=800&h=600&fit=crop&q=90",
  Megaphone: "https://images.unsplash.com/photo-1533750349088-cd871a92f312?w=800&h=600&fit=crop&q=90",
  Wrench: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop&q=90",
};

function highlightText(text) {
  if (!text) return text;
  return text.split(/(no-contract)/gi).map((part, i) =>
    part.toLowerCase() === "no-contract" ? (
      <span key={i} className="font-bold text-white text-xl">{part}</span>
    ) : part
  );
}

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

// Default fallbacks
const DEFAULTS = {
  hero: {
    title: "Become a Total Wireless Dealer — Open or Expand Your Cell Phone Store",
    content: "Partner with Prime Media Wireless, a Total Wireless Master Agent helping entrepreneurs launch, convert, and grow wireless retail businesses nationwide.",
    image_url: "https://www.paramountwirelessusa.com/wp-content/uploads/2025/06/building-2_.png",
    button_text: "Become a Dealer",
    button_link: "/become-dealer",
    metadata: { subtitle: "Premier Total Wireless Master Agent", highlight: "Retail Empire" },
  },
  stats: {
    metadata: { items: [
      { value: "500+", label: "Active Dealers" },
      { value: "$2M+", label: "Monthly Commissions" },
      { value: "30", label: "States Covered" },
      { value: "15+", label: "Years Experience" },
    ]},
  },
  features: {
    title: "Everything You Need to Succeed",
    content: "Prime Media Wireless provides comprehensive support and resources to help you build a thriving wireless retail business.",
    metadata: { subtitle: "Why Choose Us", items: [
      { icon: "Signal", title: "Verizon's 5G Network", description: "Access to America's largest and most dependable 5G network for your customers." },
      { icon: "DollarSign", title: "Competitive Commissions", description: "Industry-leading commission rates and incentive programs for maximum earnings." },
      { icon: "Users", title: "Dedicated Support", description: "24/7 support from our experienced team to help you succeed every step of the way." },
      { icon: "Award", title: "Training & Resources", description: "Comprehensive training materials and marketing resources at your fingertips." },
    ]},
  },
  benefits: {
    title: "The Prime Media Wireless Advantage",
    content: "Join hundreds of successful dealers who have transformed their businesses with our comprehensive dealer program.",
    image_url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80",
    button_text: "Start Your Application",
    button_link: "/become-dealer",
    metadata: { subtitle: "Dealer Benefits", items: [
      "No franchise fees or hidden costs",
      "Flexible store layouts and branding",
      "Premium device selection from top brands",
      "Real-time analytics and reporting",
      "Dedicated account manager",
      "Marketing support and materials",
    ], highlight_stat: { value: "+35%", label: "Avg. Revenue Growth" }},
  },
  products: {
    title: "Complete Product Portfolio",
    content: "Access the full range of Total Wireless products and services to meet every customer need.",
    metadata: { subtitle: "What We Offer", items: [
      { icon: "Signal", title: "Wireless Plans", description: "Unlimited talk, text, and data plans on Verizon's 5G network. No contracts required." },
      { icon: "Smartphone", title: "Devices", description: "Premium smartphones from Apple, Samsung, Motorola, and Google at competitive prices." },
      { icon: "Headphones", title: "Accessories", description: "Cases, chargers, headphones, and more to complete every sale and boost revenue." },
    ]},
  },
  cta: {
    title: "Ready to Start Your Wireless Business?",
    content: "Join the Prime Media Wireless family today and start building your path to success in the booming prepaid wireless industry.",
    button_text: "Apply Now",
    button_link: "/become-dealer",
    metadata: { secondary_button_text: "Contact Us", secondary_button_link: "/contact" },
  },
};

function getSection(sections, name) {
  const found = sections.find((s) => s.section_name === name && s.active !== false);
  if (!found) return DEFAULTS[name] || {};
  return { ...DEFAULTS[name], ...found, metadata: { ...(DEFAULTS[name]?.metadata || {}), ...(found.metadata || {}) } };
}

// Helper: get CMS color or return undefined (so default CSS applies)
function c(section, key) {
  return section?.metadata?.colors?.[key] || undefined;
}

export default function HomePage() {
  const [sections, setSections] = useState([]);
  const [carouselIdx, setCarouselIdx] = useState(0);

  useEffect(() => {
    getPageContent("home").then(setSections).catch(() => {});
  }, []);

  const hero = getSection(sections, "hero");
  const stats = getSection(sections, "stats");
  const features = getSection(sections, "features");
  const benefits = getSection(sections, "benefits");
  const products = getSection(sections, "products");
  const cta = getSection(sections, "cta");

  const statItems = stats.metadata?.items || [];
  const featureItems = features.metadata?.items || [];
  const benefitItems = benefits.metadata?.items || [];
  const productItems = products.metadata?.items || [];
  const highlightStat = benefits.metadata?.highlight_stat;

  // Build carousel images: use carousel_images from metadata, fallback to single image_url
  const carouselImages = benefits.metadata?.carousel_images?.length > 0
    ? benefits.metadata.carousel_images
    : benefits.image_url ? [benefits.image_url] : [];

  // Auto-advance carousel
  useEffect(() => {
    if (carouselImages.length <= 1) return;
    const timer = setInterval(() => setCarouselIdx((p) => (p + 1) % carouselImages.length), 4000);
    return () => clearInterval(timer);
  }, [carouselImages.length]);

  // Hero slideshow state
  const heroSlides = hero.metadata?.carousel_images?.length > 0
    ? hero.metadata.carousel_images
    : DEFAULT_HERO_SLIDES;
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideInterval = useRef(null);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, [heroSlides.length]);

  useEffect(() => {
    if (heroSlides.length <= 1) return;
    slideInterval.current = setInterval(nextSlide, 6000);
    return () => clearInterval(slideInterval.current);
  }, [heroSlides.length, nextSlide]);

  // Split hero title by highlight word
  const heroHighlight = hero.metadata?.highlight || "";
  let heroParts = null;
  if (heroHighlight && hero.title?.includes(heroHighlight)) {
    const idx = hero.title.indexOf(heroHighlight);
    heroParts = {
      before: hero.title.slice(0, idx),
      highlight: heroHighlight,
      after: hero.title.slice(idx + heroHighlight.length),
    };
  }

  return (
    <PublicLayout>
      <SEOHead
        pageSlug="home"
        fallback={{
          meta_title: "Prime Media Wireless | Authorized Total Wireless Master Agent & Verizon Value Dealer",
          meta_description: "Prime Media Wireless is a leading Total Wireless Master Agent and Verizon Value authorized dealer. Competitive commissions, dedicated support, training, and a complete product portfolio.",
          canonical_url: `${window.location.origin}/`,
        }}
      />
      {/* Hero Section */}
      <section className="relative bg-slate-900 h-[85vh] sm:h-[70vh] overflow-hidden" data-testid="hero-section">
        {/* Slideshow images: full-bleed, sharp, fills the entire hero box */}
        {heroSlides.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={`Prime Media Wireless - Total Wireless retail store ${i + 1}`}
            className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-[1500ms] ease-in-out"
            style={{ opacity: i === currentSlide ? 1 : 0 }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />

        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-6 sm:px-8 lg:px-12">
            <div className="max-w-xl">
            <motion.div {...fadeUp}>
              <p className="label-style font-semibold mb-3 sm:mb-4 drop-shadow-lg text-xs sm:text-sm tracking-widest" style={{color: c(hero, 'label_color') || '#ffffff'}}>{hero.metadata?.subtitle || ""}</p>
              <h1 className="font-heading font-bold text-3xl sm:text-5xl lg:text-7xl leading-tight mb-4 sm:mb-6 drop-shadow-lg" style={{color: c(hero, 'title_color') || '#ffffff', textShadow: '0 2px 8px rgba(0,0,0,0.7)'}}>
                {heroParts ? (
                  <>
                    {heroParts.before}
                    <span className="block" style={{color: c(hero, 'accent_color') || '#ffffff'}}>{heroParts.highlight}</span>
                    {heroParts.after}
                  </>
                ) : (
                  hero.title
                )}
              </h1>
              <p className="text-sm sm:text-base lg:text-lg mb-6 sm:mb-8 max-w-xl font-body drop-shadow-md leading-relaxed" style={{color: c(hero, 'text_color') || '#ffffff', textShadow: '0 1px 4px rgba(0,0,0,0.6)'}}>
                {highlightText(hero.content)}
              </p>
              <div className="flex flex-wrap gap-4">
                {hero.button_text && (
                  <Link to={hero.button_link || "/become-dealer"}>
                    <Button size="lg" className="bg-teal hover:bg-teal-dark text-white font-semibold px-6 sm:px-8 text-sm sm:text-base" data-testid="hero-become-dealer-btn">
                      {hero.button_text}
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                    </Button>
                  </Link>
                )}
              </div>
            </motion.div>
            </div>
          </div>
        </div>

        {/* Slide indicators */}
      </section>

      {/* Stats Section */}
      {statItems.length > 0 && (
        <section className="bg-white py-12 border-b border-slate-200" data-testid="stats-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {statItems.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <p className="font-heading font-bold text-3xl lg:text-4xl" style={{color: c(stats, 'accent_color') || '#00B2A9'}}>{stat.value}</p>
                  <p className="font-body mt-1" style={{color: c(stats, 'text_color') || '#475569'}}>{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Awards & Recognition — anchored by the 2026 Master Agent Award –
          Growth Champion from Total Wireless (Verizon Value). */}
      <AwardsSection />

      {/* Why Choose Prime Media Wireless — the six-pillar recruitment pitch,
          led by the #1 Growth Champion award. */}
      <WhyChoosePmw />

      {/* Features / Why Choose Us Section */}
      {featureItems.length > 0 && (
        <section className="py-20 bg-slate-50" data-testid="features-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="label-style mb-2" style={{color: c(features, 'label_color') || '#00B2A9'}}>{features.metadata?.subtitle || ""}</p>
              <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-4" style={{color: c(features, 'title_color') || '#0B1B3D'}}>{features.title}</h2>
              <p className="max-w-2xl mx-auto font-body" style={{color: c(features, 'text_color') || '#475569'}}>{features.content}</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featureItems.map((feature, index) => {
                const imgSrc = feature.image_url || FEATURE_IMAGES[feature.icon];
                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl border border-slate-200 hover-lift overflow-hidden"
                  >
                    {imgSrc && (
                      <div className="h-36 overflow-hidden">
                        <img src={imgSrc} alt={feature.title} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="font-heading font-semibold text-lg mb-2" style={{color: c(features, 'title_color') || '#0B1B3D'}}>{feature.title}</h3>
                      <p className="text-sm font-body" style={{color: c(features, 'text_color') || '#475569'}}>{feature.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      {benefitItems.length > 0 && (
        <section className="py-20 bg-white" data-testid="benefits-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="label-style mb-2" style={{color: c(benefits, 'label_color') || '#00B2A9'}}>{benefits.metadata?.subtitle || ""}</p>
                <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-6" style={{color: c(benefits, 'title_color') || '#0B1B3D'}}>{benefits.title}</h2>
                <p className="mb-8 font-body" style={{color: c(benefits, 'text_color') || '#475569'}}>{benefits.content}</p>
                <ul className="space-y-3 mb-8">
                  {benefitItems.map((benefit) => (
                    <li key={benefit} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 flex-shrink-0" style={{color: c(benefits, 'accent_color') || '#00B2A9'}} />
                      <span className="font-body" style={{color: c(benefits, 'text_color') || '#334155'}}>{benefit}</span>
                    </li>
                  ))}
                </ul>
                {benefits.button_text && (
                  <Link to={benefits.button_link || "/become-dealer"}>
                    <Button className="bg-navy hover:bg-navy-light text-white" data-testid="benefits-cta-btn">
                      {benefits.button_text}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                )}
              </div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                {benefits.metadata?.video_url ? (
                  <div className="relative rounded-2xl shadow-xl" data-testid="benefits-video">
                    <video
                      src={benefits.metadata.video_url}
                      poster={benefits.metadata.video_poster || benefits.image_url || "https://primemediawireless.com/og-default.jpg"}
                      className="w-full h-auto block rounded-2xl"
                      controls
                      loop
                      playsInline
                      preload="metadata"
                      data-testid="benefits-video-player"
                    />
                    {/* Google VideoObject schema for Search Console video indexing */}
                    <script
                      type="application/ld+json"
                      dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                          "@context": "https://schema.org",
                          "@type": "VideoObject",
                          "name": benefits.title || "Prime Media Wireless — Dealer Benefits",
                          "description": benefits.content ? String(benefits.content).replace(/<[^>]+>/g, " ").slice(0, 280) : "Benefits of partnering with Prime Media Wireless as an authorized Total Wireless dealer.",
                          "thumbnailUrl": benefits.metadata.video_poster || benefits.image_url || "https://primemediawireless.com/og-default.jpg",
                          "contentUrl": benefits.metadata.video_url,
                          "uploadDate": benefits.updated_at || benefits.created_at || "2025-01-01",
                          "publisher": {
                            "@type": "Organization",
                            "name": "Prime Media Wireless",
                            "logo": { "@type": "ImageObject", "url": "https://primemediawireless.com/logo512.png" },
                          },
                        }),
                      }}
                    />
                  </div>
                ) : carouselImages.length > 0 ? (
                  <div className="relative overflow-hidden rounded-2xl shadow-xl" data-testid="benefits-carousel">
                    <div className="relative aspect-[4/3]">
                      {carouselImages.map((img, idx) => (
                        <img
                          key={idx}
                          src={img}
                          alt={`Business showcase ${idx + 1}`}
                          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${idx === carouselIdx % carouselImages.length ? "opacity-100" : "opacity-0"}`}
                        />
                      ))}
                    </div>
                    {carouselImages.length > 1 && (
                      <>
                        <button onClick={() => setCarouselIdx((p) => (p - 1 + carouselImages.length) % carouselImages.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow-md transition-colors" data-testid="carousel-prev"><ChevronLeft className="w-5 h-5 text-navy" /></button>
                        <button onClick={() => setCarouselIdx((p) => (p + 1) % carouselImages.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow-md transition-colors" data-testid="carousel-next"><ChevronRight className="w-5 h-5 text-navy" /></button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                          {carouselImages.map((_, idx) => (
                            <button key={idx} onClick={() => setCarouselIdx(idx)} className={`w-2 h-2 rounded-full transition-all ${idx === carouselIdx % carouselImages.length ? "bg-white w-4" : "bg-white/50"}`} data-testid={`carousel-dot-${idx}`} />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
                {highlightStat && highlightStat.value && (
                  <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg border border-slate-200">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-teal" />
                      </div>
                      <div>
                        <p className="font-heading font-bold text-navy">{highlightStat.value}</p>
                        <p className="text-slate-500 text-sm font-body">{highlightStat.label}</p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Products Section */}
      {productItems.length > 0 && (
        <section className="py-20 bg-navy" data-testid="products-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="label-style mb-2" style={{color: c(products, 'label_color') || '#00B2A9'}}>{products.metadata?.subtitle || ""}</p>
              <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-4" style={{color: c(products, 'title_color') || '#ffffff'}}>{products.title}</h2>
              <p className="max-w-2xl mx-auto font-body" style={{color: c(products, 'text_color') || '#94a3b8'}}>{products.content}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {productItems.map((item, index) => {
                const Icon = ICON_MAP[item.icon] || Signal;
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/5 backdrop-blur border border-white/10 p-6 rounded-xl hover:bg-white/10 transition-colors"
                  >
                    <div className="w-12 h-12 bg-teal rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-navy" />
                    </div>
                    <h3 className="font-heading font-semibold text-white text-xl mb-2">{item.title}</h3>
                    <p className="text-slate-400 font-body">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Dealer Growth Support — 3-card row linking to new keyword landing pages */}
      <section className="py-20 bg-white border-t border-slate-100" data-testid="dealer-support-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <p className="label-style mb-2 text-teal">Dealer Growth Support</p>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-4 text-navy">Financing, Incentives &amp; Multi-Store Growth</h2>
            <p className="max-w-2xl mx-auto font-body text-slate-600">
              Whether you're funding your first Total Wireless store, unlocking dealer incentives, or building a multi-store portfolio — Prime Media Wireless has a playbook and a partner for every stage.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/wireless-store-financing"
              className="group block bg-slate-50 hover:bg-navy hover:text-white border border-slate-200 hover:border-navy p-8 rounded-xl transition-all"
              data-testid="dealer-support-financing-card"
            >
              <div className="w-12 h-12 bg-teal group-hover:bg-white rounded-lg flex items-center justify-center mb-5 transition-colors">
                <span className="text-navy font-bold text-xl">$</span>
              </div>
              <h3 className="font-heading font-semibold text-xl mb-2 group-hover:text-white text-navy">Store Financing</h3>
              <p className="font-body text-slate-600 group-hover:text-slate-200 mb-4">SBA and specialty lender intros, inventory financing, and startup capital paths so you can open without draining savings.</p>
              <span className="inline-flex items-center gap-2 font-semibold text-teal group-hover:text-white">
                Explore financing <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link
              to="/total-wireless-dealer-incentives"
              className="group block bg-slate-50 hover:bg-navy hover:text-white border border-slate-200 hover:border-navy p-8 rounded-xl transition-all"
              data-testid="dealer-support-incentives-card"
            >
              <div className="w-12 h-12 bg-teal group-hover:bg-white rounded-lg flex items-center justify-center mb-5 transition-colors">
                <span className="text-navy font-bold text-xl">%</span>
              </div>
              <h3 className="font-heading font-semibold text-xl mb-2 group-hover:text-white text-navy">Dealer Incentives</h3>
              <p className="font-body text-slate-600 group-hover:text-slate-200 mb-4">Store opening bonus, co-op marketing, signage reimbursement, MDF, and multi-store bonus programs available to qualified operators.</p>
              <span className="inline-flex items-center gap-2 font-semibold text-teal group-hover:text-white">
                See incentives <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link
              to="/multi-store-wireless-operator"
              className="group block bg-slate-50 hover:bg-navy hover:text-white border border-slate-200 hover:border-navy p-8 rounded-xl transition-all"
              data-testid="dealer-support-multistore-card"
            >
              <div className="w-12 h-12 bg-teal group-hover:bg-white rounded-lg flex items-center justify-center mb-5 transition-colors">
                <span className="text-navy font-bold text-xl">#</span>
              </div>
              <h3 className="font-heading font-semibold text-xl mb-2 group-hover:text-white text-navy">Multi-Store Growth</h3>
              <p className="font-body text-slate-600 group-hover:text-slate-200 mb-4">Protected territory, priority inventory, and a scaling playbook to grow from one Total Wireless store to a portfolio.</p>
              <span className="inline-flex items-center gap-2 font-semibold text-teal group-hover:text-white">
                Plan your next store <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-4" style={{color: c(cta, 'title_color') || '#ffffff'}}>{cta.title}</h2>
          <p className="max-w-2xl mx-auto mb-8 font-body" style={{color: c(cta, 'text_color') || 'rgba(255,255,255,0.8)'}}>{cta.content}</p>
          <div className="flex flex-wrap justify-center gap-4">
            {cta.button_text && (
              <Link to={cta.button_link || "/become-dealer"}>
                <Button size="lg" className="bg-navy hover:bg-navy-dark text-white font-semibold px-8" data-testid="cta-apply-btn">
                  {cta.button_text}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            )}
            {cta.metadata?.secondary_button_text && (
              <Link to={cta.metadata?.secondary_button_link || "/contact"}>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="cta-contact-btn">
                  {cta.metadata.secondary_button_text}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
